import {createStore} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {MediaItem} from '@common/player/media-item';
import {setInLocalStorage as _setInLocalStorage} from '@ui/utils/hooks/local-storage';
import {shuffleArray} from '@ui/utils/array/shuffle-array';
import {PlayerStoreOptions} from '@common/player/state/player-store-options';
import {
  getPlayerStateFromLocalStorage,
  type PlayerInitialData,
} from '@common/player/utils/player-local-storage';
import {prependToArrayAtIndex} from '@ui/utils/array/prepend-to-array-at-index';
import deepMerge from 'deepmerge';
import {resetMediaSession} from '@common/player/utils/reset-media-session';
import {playerQueue} from '@common/player/player-queue';
import {playerLog} from '@common/player/player-log';
import {playerAd} from '@common/player/player-ad';
import type {
  PlaybackEngine,
  PlayerState,
  ProviderListeners,
  RepeatMode,
} from '@common/player/state/player-state';
import {handlePlayerKeybinds} from '@common/player/handle-player-keybinds';
import {initPlayerMediaSession} from '@common/player/utils/init-player-media-session';
import {isSameMedia} from '@common/player/utils/is-same-media';
import {
  createFullscreenSlice,
  FullscreenSlice,
} from '@common/player/state/fullscreen/fullscreen-slice';
import {createPipSlice, PipSlice} from '@common/player/state/pip/pip-slice';
import {subscribeWithSelector} from 'zustand/middleware';
import { logEvent } from '@common/core/event-logger';
import {
  playerOverlayState
} from '@app/web-player/state/player-overlay-store';
import { getUserRolesAccess } from '@common/auth/user-roles-access';
import {openDialog} from '@ui/overlays/store/dialog-store';
import {PlayerControlLockedDialog} from '@common/player/ui/modals/player-control-locked-dialog';
import {MODAL_SURFACE_Z_INDEX} from '@ui/overlays/overlay-z-index';
import type {PlayerQuotaAction} from '@common/player/quotas/check-player-action-quota';
import {
  createLocalPlayerQuotaCache,
} from '@common/player/quotas/local-player-quota-cache';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {
  canUseNativeAudioProvider,
  canUseNativeAudioPlaybackForMedia,
  canUseNativeAudioPlaybackForQueue,
} from '@common/player/utils/can-use-native-audio-provider';
import {nativePlaybackBridge} from '@common/core/capacitor/native-playback-bridge';
import {
  createLimitPromptSignature,
  mapLimitTypeToPromptAction,
  type PlayerLimitPromptPayload,
} from '@common/player/limits/native-limit-reached';

function mediaCanBeRestoredFromNativeSession(media?: MediaItem): boolean {
  return canUseNativeAudioPlaybackForMedia(media);
}

function queueCanBeDelegatedToNative(queue?: MediaItem[]): boolean {
  return canUseNativeAudioPlaybackForQueue(queue);
}

function isNativePlaybackEngineActive(
  engine: PlaybackEngine | undefined,
): boolean {
  return engine === 'native' && canUseNativeAudioProvider();
}

function sanitizeInitialPlayerData(initialData: PlayerInitialData): {
  data: PlayerInitialData;
  removedAdItems: boolean;
  clearedNativeAudioQueue: boolean;
} {
  const queue = initialData.queue ?? [];
  if (!queue.length) {
    return {
      data: initialData,
      removedAdItems: false,
      clearedNativeAudioQueue: false,
    };
  }

  const sanitizedQueue = queue.filter(media => !media?.isAd);
  const removedAdItems = sanitizedQueue.length !== queue.length;
  const sanitizedCuedMediaId = sanitizedQueue.some(
    media => `${media.id}` === `${initialData.cuedMediaId ?? ''}`,
  )
    ? initialData.cuedMediaId
    : sanitizedQueue[0]?.id;

  const mediaIdToRestore =
    sanitizedCuedMediaId ?? sanitizedQueue[0]?.id;
  const mediaToRestore = sanitizedQueue.find(
    media => `${media.id}` === `${mediaIdToRestore ?? ''}`,
  );
  const clearedNativeAudioQueue = Boolean(
    sanitizedQueue.length &&
    mediaToRestore &&
      mediaCanBeRestoredFromNativeSession(mediaToRestore) &&
      queueCanBeDelegatedToNative(sanitizedQueue) &&
      canUseNativeAudioProvider(),
  );

  return {
    data: {
      ...initialData,
      queue: clearedNativeAudioQueue ? [] : sanitizedQueue,
      cuedMediaId: clearedNativeAudioQueue ? undefined : sanitizedCuedMediaId,
    },
    removedAdItems,
    clearedNativeAudioQueue,
  };
}

async function shouldDeferInitialCueToNativeSession(
  mediaToCue?: MediaItem,
  persistedQueue?: MediaItem[],
): Promise<boolean> {
  if (canUseNativeAudioProvider()) {
    try {
      const nativeSnapshot = await nativePlaybackBridge.getState();
      const hasUsableNativeAudioSession =
        nativeSnapshot.state.currentItem?.playbackKind === 'audio' ||
        nativeSnapshot.queue.items.some(item => item.playbackKind === 'audio');

      if (hasUsableNativeAudioSession) {
        console.info(
          '[PlayerStore] defer persisted queue restore because native playback session already exists',
          {
            nativeCurrentItemId: nativeSnapshot.state.currentItem?.id,
            nativeQueueLength: nativeSnapshot.queue.items.length,
          },
        );
        return true;
      }
    } catch (error) {
      console.warn(
        '[PlayerStore] native playback state unavailable during initial restore check',
        error,
      );
    }
  }

  if (
    !mediaCanBeRestoredFromNativeSession(mediaToCue) ||
    !queueCanBeDelegatedToNative(persistedQueue)
  ) {
    return false;
  }

  console.info(
    '[PlayerStore] skip persisted queue restore while native playback provider bootstraps',
    {
      persistedMediaId: mediaToCue?.id,
      persistedMediaGroupId: mediaToCue?.groupId,
    },
  );
  return true;
}

export const createPlayerStore = (
  id: string | number,
  options: PlayerStoreOptions,
) => {
  // initialData from options should take priority over local storage data
  const mergedInitialData = deepMerge(
    getPlayerStateFromLocalStorage(id, options),
    options.initialData || {},
  );
  const {data: initialData, removedAdItems, clearedNativeAudioQueue} =
    sanitizeInitialPlayerData(mergedInitialData);

  const setInLocalStorage = (key: string, value: any) => {
    _setInLocalStorage(`player.${id}.${key}`, value);
  };

function maybeLogSkip(state: PlayerState) {
  if (isNativePlaybackEngineActive(state.playbackEngine)) {
    return;
  }

  const duration = state.mediaDuration;
  const currentTime = state.getCurrentTime?.();
  const media = state.cuedMedia;

    if (!media?.id || !duration || !currentTime || duration === 0) return;

    const percent = Math.floor((currentTime / duration) * 100);

    let event_type: string | null = null;
    if (percent < 10) {
      event_type = 'skip_10';
    } else if (percent < 50) {
      event_type = 'skip_50';
    }

    if (event_type) {
      logEvent({
        event_type,
        entity_id: media.id,
        entity_type: 'track',
        metadata: {
          track: {...media.meta},
          percent,
          time: Math.floor(currentTime),
          volume: state.volume,
        },
      });
    }
  }

  return createStore<PlayerState & FullscreenSlice & PipSlice>()(
    subscribeWithSelector(
      immer((set, get, store) => {
        const {userAccess} = getUserRolesAccess();
        const log = playerLog(get);
        const ad = playerAd(get);
        
        let adItems: MediaItem[];
        let currentMedia: MediaItem;
        let timeoutSetVolume: number;
        let controlLockDialogPromise: Promise<any> | null = null;
        let listenQuotaIntervalId: number | null = null;
        let activeNativeLimitPromptSignature: string | null = null;

        const shouldReactOwnPlaybackLogic = () =>
          !isNativePlaybackEngineActive(get().playbackEngine);

        const getQueueForNativeDelegation = (mediaItems?: MediaItem[]) => {
          if (mediaItems?.length) {
            return mediaItems;
          }

          if (get().shuffledQueue.length) {
            return get().shuffledQueue;
          }

          const cuedMedia = get().cuedMedia;
          return cuedMedia ? [cuedMedia] : [];
        };

        const getQueueForNativeMirror = (
          targetMedia?: MediaItem,
          mediaItems?: MediaItem[],
        ) => {
          const sourceQueue = getQueueForNativeDelegation(mediaItems);
          const mirroredQueue = sourceQueue.filter(media =>
            mediaCanBeRestoredFromNativeSession(media),
          );

          if (
            targetMedia &&
            mediaCanBeRestoredFromNativeSession(targetMedia) &&
            !mirroredQueue.some(media => isSameMedia(media, targetMedia))
          ) {
            mirroredQueue.unshift(targetMedia);
          }

          const startIndex = targetMedia
            ? Math.max(
                0,
                mirroredQueue.findIndex(media => isSameMedia(media, targetMedia)),
              )
            : 0;

          return {
            queue: mirroredQueue,
            startIndex,
          };
        };

        const canRouteMediaToNative = (media?: MediaItem) => {
          if (!canUseNativeAudioProvider()) {
            return false;
          }

          return mediaCanBeRestoredFromNativeSession(media);
        };

        const canDelegateQueueIntentToNative = (mediaItems?: MediaItem[]) => {
          if (!canUseNativeAudioProvider()) {
            return false;
          }

          const providerApi = get().providerApi;
          if (!providerApi?.replaceQueue) {
            return false;
          }

          return queueCanBeDelegatedToNative(
            getQueueForNativeDelegation(mediaItems),
          );
        };

        const canDelegateTransportIntentToNative = () => {
          if (!canUseNativeAudioProvider()) {
            return false;
          }

          if (
            !queueCanBeDelegatedToNative(getQueueForNativeDelegation())
          ) {
            return false;
          }

          const providerApi = get().providerApi;
          return Boolean(
            providerApi?.play &&
              providerApi?.skipToNext &&
              providerApi?.skipToPrevious &&
              providerApi?.skipToIndex,
          );
        };

        const waitForActiveProviderApi = async (
          timeoutMs: number = 1000,
        ) => {
          const immediateProviderApi = get().providerApi;
          if (immediateProviderApi?.play) {
            return immediateProviderApi;
          }

          return new Promise<typeof immediateProviderApi>(resolve => {
            const timeoutId = setTimeout(() => {
              unsubscribe();
              resolve(get().providerApi);
            }, timeoutMs);

            const unsubscribe = get().subscribe({
              providerReady: () => {
                clearTimeout(timeoutId);
                unsubscribe();
                resolve(get().providerApi);
              },
              error: () => {
                clearTimeout(timeoutId);
                unsubscribe();
                resolve(get().providerApi);
              },
            });
          });
        };

        const logReactPlaybackEvent = (
          payload: Parameters<typeof logEvent>[0],
        ) => {
          if (!shouldReactOwnPlaybackLogic()) {
            return;
          }

          logEvent(payload);
        };

        const parseRestrictionBool = (value: unknown): boolean => {
          if (value == null || value === '') {
            return true;
          }
          if (typeof value === 'boolean') {
            return value;
          }
          if (typeof value === 'number') {
            return value !== 0;
          }
          if (typeof value === 'string') {
            const normalized = value.toLowerCase();
            return !['false', '0', 'off', 'no'].includes(normalized);
          }
          return Boolean(value);
        };

        const parseRestrictionNumber = (value: unknown): number | null => {
          if (value == null || value === '') {
            return null;
          }
          if (typeof value === 'boolean') {
            return null;
          }
          if (!Number.isFinite(Number(value))) {
            return null;
          }
          const normalized = Math.floor(Number(value));
          return normalized >= 0 ? normalized : null;
        };
        const normalizeRepeatMode = (value: unknown): RepeatMode => {
          if (value === 'all' || value === 'one' || value === false) {
            return value;
          }
          return 'all';
        };
        const canUseShuffleControl =
          userAccess.medias.play.base &&
          parseRestrictionBool(userAccess.medias.play.canShuffle);
        const canUseRepeatControl =
          userAccess.medias.play.base &&
          parseRestrictionBool(userAccess.medias.play.canRepeat);
        const initialShuffling = canUseShuffleControl
          ? Boolean(initialData.state?.shuffling)
          : false;
        const initialRepeat = canUseRepeatControl
          ? normalizeRepeatMode(initialData.state?.repeat)
          : false;
        const rawPreviewLimitSeconds = parseRestrictionNumber(
          userAccess.medias.play.preview,
        );
        const previewLimitSeconds = rawPreviewLimitSeconds;
        const isPreviewMode = previewLimitSeconds != null;
        const hasZeroSecondPreviewLimit = previewLimitSeconds === 0;

        const openControlLockedDialog = (payload: {
          action: string;
          reason: string;
          retryAfterSeconds?: number | null;
        }) => {
          if (!getBootstrapData().user) {
            get().openSignUpModal();
            return;
          }

          if (controlLockDialogPromise) {
            return;
          }

          controlLockDialogPromise = openDialog(PlayerControlLockedDialog, {
            action: payload.action,
            reason: payload.reason,
            retryAfterSeconds: payload.retryAfterSeconds ?? null,
            track: get().cuedMedia?.meta,
            overlayZIndex: MODAL_SURFACE_Z_INDEX,
          }).finally(() => {
            controlLockDialogPromise = null;
          });
        };

        const trackLimitPromptOpened = (payload: PlayerLimitPromptPayload) => {
          const currentTrack = get().cuedMedia?.meta;
          const entityId = currentTrack?.id;
          void logEvent({
            event_type: 'player_limit_prompt_opened',
            ...(entityId
              ? {
                  entity_id: entityId,
                  entity_type: 'track',
                }
              : {}),
            metadata: {
              identifier: payload.identifier,
              kind: payload.kind,
              limitType: payload.limitType,
              promptType: payload.promptType,
              isAuthenticated: payload.isAuthenticated,
              requiresAccountCreation: payload.requiresAccountCreation,
              source: payload.source,
              ...(payload.surface ? {surface: payload.surface} : {}),
            },
          }).catch(() => undefined);
        };

        const handleNativeLimitReached = (payload: PlayerLimitPromptPayload) => {
          const signature = createLimitPromptSignature(payload);
          if (activeNativeLimitPromptSignature === signature) {
            return;
          }

          if (get().isSignUpModalOpen || controlLockDialogPromise) {
            return;
          }

          trackLimitPromptOpened(payload);

          if (payload.promptType === 'createAccount') {
            activeNativeLimitPromptSignature = signature;
            get().openSignUpModal();
            return;
          }

          const action = mapLimitTypeToPromptAction(payload.limitType);
          activeNativeLimitPromptSignature = signature;
          controlLockDialogPromise = openDialog(PlayerControlLockedDialog, {
            action,
            reason: 'overQuota',
            retryAfterSeconds: null,
            track: get().cuedMedia?.meta,
            overlayZIndex: MODAL_SURFACE_Z_INDEX,
            limitType: payload.limitType,
            titleKey: payload.titleKey,
            messageKey: payload.messageKey,
            actionKey: payload.actionKey,
          }).finally(() => {
            controlLockDialogPromise = null;
            if (activeNativeLimitPromptSignature === signature) {
              activeNativeLimitPromptSignature = null;
            }
          });
        };

        const localQuotaCache = createLocalPlayerQuotaCache({
          storageKey: `player.${id}.action-quotas`,
          userKey: String(getBootstrapData().user?.id ?? 'guest'),
          defaults: {
            next: {
              limit: parseRestrictionNumber(userAccess.medias.play.nextPerHour),
              windowSeconds: 3600,
            },
            prev: {
              limit: parseRestrictionNumber(userAccess.medias.play.prevPerHour),
              windowSeconds: 3600,
            },
            listen24h: {
              limit: parseRestrictionNumber(
                userAccess.medias.play.listen24hMaxSeconds,
              ),
              windowSeconds: 86400,
            },
          },
        });
        const queue = playerQueue(get);
        const queueAutoFillEnabled =
          userAccess.medias.play.base &&
          parseRestrictionBool(userAccess.medias.play.queueAutoFill);
        let autoFillPromise: Promise<void> | null = null;
        let lastAutoFillSeed: string | null = null;

        const shouldPrefillQueue = (): boolean => {
          const queueSize = get().shuffledQueue.length;
          if (!queueSize) {
            return false;
          }
          const pointer = queue.getPointer();
          return pointer >= Math.max(0, queueSize - 2);
        };

        const maybeAutoFillQueue = () => {
          if (!queueAutoFillEnabled || !options.loadMoreMediaItems) {
            return;
          }
          if (!shouldPrefillQueue()) {
            return;
          }

          const seedMedia = queue.getCurrent();
          const seedKey = `${seedMedia?.id ?? 'none'}|${seedMedia?.groupId ?? 'none'}|${queue.getPointer()}|${get().shuffledQueue.length}`;
          if (autoFillPromise && lastAutoFillSeed === seedKey) {
            return;
          }

          lastAutoFillSeed = seedKey;
          autoFillPromise = (async () => {
            const items = await options.loadMoreMediaItems?.(seedMedia);
            if (!items?.length) {
              return;
            }

            const existingIds = new Set(
              get().shuffledQueue.map(item => `${item.id}`),
            );
            const uniqueItems = items.filter(
              item => !existingIds.has(`${item.id}`),
            );
            if (!uniqueItems.length) {
              return;
            }

            // Keep the upcoming last track in place, append new items at end.
            get().addToQueue(
              uniqueItems,
              Math.max(0, get().shuffledQueue.length - 1),
              false,
            );
          })().finally(() => {
            autoFillPromise = null;
          });
        };

        const shouldPromptQueueAutoFillAtEnd = (): boolean => {
          const state = get();
          return (
            !queueAutoFillEnabled &&
            !state.cuedMedia?.isAd &&
            state.repeat === false &&
            queue.isLast()
          );
        };

        const stopListenQuotaTimer = () => {
          if (listenQuotaIntervalId == null || typeof window === 'undefined') {
            return;
          }
          window.clearInterval(listenQuotaIntervalId);
          listenQuotaIntervalId = null;
        };

        const startListenQuotaTimer = () => {
          if (!shouldReactOwnPlaybackLogic()) {
            return;
          }

          if (listenQuotaIntervalId != null || typeof window === 'undefined') {
            return;
          }

          listenQuotaIntervalId = window.setInterval(() => {
            const state = get();
            if (!state.isPlaying || state.isBuffering || !state.cuedMedia) {
              return;
            }
            if (state.cuedMedia.isAd) {
              return;
            }

            const decision = localQuotaCache.consumeListenSeconds(1);
            if (!decision.allowed) {
              stopListenQuotaTimer();
              state.pause(false);
              openControlLockedDialog({
                action: 'play',
                reason: decision.reason,
                retryAfterSeconds: decision.retryAfterSeconds,
              });
            }
          }, 1000);
        };

        const isActionAllowedByStaticRestriction = (
          action: PlayerQuotaAction,
        ): boolean => {
          switch (action) {
            case 'seek':
              return parseRestrictionBool(userAccess.medias.play.canSeek);
            case 'shuffle':
              return parseRestrictionBool(userAccess.medias.play.canShuffle);
            case 'repeat':
              return parseRestrictionBool(userAccess.medias.play.canRepeat);
            case 'queue_auto_fill':
              return parseRestrictionBool(userAccess.medias.play.queueAutoFill);
            case 'queue_auto_mix':
              return !parseRestrictionBool(
                userAccess.medias.play.canQueueFullAlbum,
              );
            default:
              return true;
          }
        };

        const ensureUserActionAllowed = (
          action: PlayerQuotaAction,
          consume = true,
        ): boolean => {
          if (!shouldReactOwnPlaybackLogic()) {
            return true;
          }

          if (!userAccess.medias.play.base) {
            openControlLockedDialog({action, reason: 'noPermission'});
            return false;
          }

          if (
            action === 'seek' ||
            action === 'shuffle' ||
            action === 'repeat' ||
            action === 'queue_auto_fill' ||
            action === 'queue_auto_mix'
          ) {
            const allowed = isActionAllowedByStaticRestriction(action);
            if (!allowed) {
              openControlLockedDialog({action, reason: 'noPermission'});
            }
            return allowed;
          }

          if (action === 'next' || action === 'prev') {
            const localDecision = localQuotaCache.checkAndConsume(action, consume);
            if (!localDecision.allowed) {
              openControlLockedDialog({
                action,
                reason: localDecision.reason,
                retryAfterSeconds: localDecision.retryAfterSeconds,
              });
              return false;
            }

            return true;
          }

          if (action === 'play') {
            const localDecision = localQuotaCache.checkListenLimit();
            if (!localDecision.allowed) {
              openControlLockedDialog({
                action,
                reason: localDecision.reason,
                retryAfterSeconds: localDecision.retryAfterSeconds,
              });
              return false;
            }
            return true;
          }

          return true;
        };
        
        const listeners = new Set<Partial<ProviderListeners>>();
        const internalListeners: Partial<ProviderListeners> = {
          cued: () => {
            if (!options.persistQueueInLocalStorage) {
              return;
            }

            const currentMedia = get().cuedMedia;
            if (currentMedia?.id != null) {
              setInLocalStorage('cuedMediaId', currentMedia.id);
            }
          },
          play: () => {
            set(s => {
              s.isPlaying = true;
              s.playbackStarted = true;
            });
            if (shouldReactOwnPlaybackLogic()) {
              startListenQuotaTimer();
            }
          },
          pause: () => {
            set(s => {
              s.isPlaying = false;
              s.controlsVisible = true;
            });
            stopListenQuotaTimer();
            void localQuotaCache.flushToStorage();
          },
          error: e => {
            set(s => {
              // there could be a number of non-fatal errors where player will continue to
              // work properly, like autoplay error from HTML5 video or buffer full from HLS
              if (e?.fatal) {
                s.isPlaying = false;
              }
            });
            if (e?.fatal) {
              stopListenQuotaTimer();
              void localQuotaCache.flushToStorage();
            }
          },
          progress: payload => {
            if (
              isPreviewMode &&
              !isNativePlaybackEngineActive(get().playbackEngine)
            ) {
              log.stopTimer();
              ad.stopTimer();

              if (payload.currentTime >= (previewLimitSeconds ?? 0)) {
                get().playNext();
              }
            }
          },
          durationChange: payload => {
            set({
              mediaDuration:
                isPreviewMode && shouldReactOwnPlaybackLogic()
                ? (previewLimitSeconds ?? payload.duration)
                : payload.duration,
            });
          },
          streamTypeChange: payload => {
            // Warning: Received `true` for a non-boolean attribute `max`. (WTF ???)
            set({streamType: payload.streamType});
          },
          buffered: payload => {
            // 
          },
          playbackRateChange: payload => {
            set({playbackRate: payload.rate});
          },
          playbackRates: ({rates}) => {
            set({playbackRates: rates});
          },
          playbackQualities: ({qualities}) => {
            set({playbackQualities: qualities});
          },
          audioTracks: ({tracks}) => {
            set({audioTracks: tracks});
          },
          currentAudioTrackChange: ({trackId}) => {
            set({currentAudioTrack: trackId});
          },
          playbackQualityChange: ({quality}) => {
            set({playbackQuality: quality});
          },
          textTracks: ({tracks}) => {
            set({textTracks: tracks});
          },
          currentTextTrackChange: ({trackId}) => {
            set({currentTextTrack: trackId});
          },
          textTrackVisibilityChange: ({isVisible}) => {
            set({textTrackIsVisible: isVisible});
          },
          buffering: ({isBuffering}) => {
            set({isBuffering});
          },
          playbackEnd: async () => {
            // console.log('playbackEnd');
            let media = get().cuedMedia;
            const shouldPromptQueueAutoFillUpgrade =
              shouldPromptQueueAutoFillAtEnd();

            if (isNativePlaybackEngineActive(get().playbackEngine)) {
              return;
            }
            
            // don't play next or repeat while seeking via seekbar
            if (get().isSeeking) return;
            
            if (media?.isAd) {
              const nextMedia = queue.getNext();

              ad.resetAd();
              ad.resetTimer();

              if (adItems) {
                get().removeFromQueue(adItems);
                adItems = [];
              }

              if (nextMedia) {
                await get().play(nextMedia);
              } else {
                set({controlsLocked: false});
                get().stop(false);
              }

              return;
            }

            await get().playNext();
            if (shouldPromptQueueAutoFillUpgrade) {
              openControlLockedDialog({
                action: 'queue_auto_fill',
                reason: 'noPermission',
              });
            }

            // Laisser après le play next, sinon, il delete le titre de la playlist puis lit le premier
          },
          posterLoaded: ({url}) => {
            set({posterUrl: url});
          },
          providerReady: () => {
            const provider = get().providerApi;
            if (provider) {
              provider.setVolume(get().volume);
              provider.setMuted(get().muted);
              if (
                options.autoPlay &&
                !isNativePlaybackEngineActive(get().playbackEngine)
              ) {
                provider.play();
              }
              set({providerReady: true});
            }
          },
        };

        const keybindsHandler = (e: KeyboardEvent) => {
          handlePlayerKeybinds(e, get);
        };

        const initialQueue = initialData.queue || [];
        return {
          options,
          ...createFullscreenSlice(set, get, store, listeners),
          ...createPipSlice(set, get, store, listeners),
          originalQueue: initialQueue,
          shuffledQueue: initialShuffling ? shuffleArray(initialQueue) : initialQueue,
          playbackEngine: 'web',
          isPlaying: false,
          isBuffering: false,
          streamType: null,
          playbackStarted: false,
          providerReady: false,
          showLoader: true,
          pauseWhileSeeking: options.pauseWhileSeeking ?? true,
          isSeeking: false,
          setIsSeeking: (isSeeking: boolean) => {
            set({isSeeking});
          },
          controlsVisible: true,
          controlsLocked: false,
          setControlsVisible: (isVisible: boolean) => {
            set(s => {
              s.controlsVisible = isVisible;
            });
          },
          volume: initialData.state?.volume ?? 30,
          setVolume: value => {
            get().providerApi?.setVolume(value);
            set(s => {
              s.volume = value;
            });
            setInLocalStorage('volume', value);

            if(get().isPlaying && get().cuedMedia) {
              clearTimeout(timeoutSetVolume);
              timeoutSetVolume = window.setTimeout(() => {
                logReactPlaybackEvent({
                  event_type: 'set_volume',
                  entity_id: get().cuedMedia?.id,
                  entity_type: 'track',
                  metadata: {
                    track: {...get().cuedMedia?.meta},
                    volume: value
                  }
                });
              }, 500);
            }
          },
          muted: initialData.state?.muted ?? false,
          setMuted: isMuted => {
            get().providerApi?.setMuted(isMuted);
            set(s => {
              s.muted = isMuted;
            });
            setInLocalStorage('muted', isMuted);

            logReactPlaybackEvent({
              event_type: 'set_volume',
              entity_id: get().cuedMedia?.id,
              entity_type: 'track',
              metadata: {
                track: {...get().cuedMedia?.meta},
                volume: 0
              }
            });
          },
          playbackRates: [],
          playbackRate: 2,
          setPlaybackRate: speed => {
            get().providerApi?.setPlaybackRate(speed);
          },
          playbackQuality: 'auto',
          setPlaybackQuality: quality => {
            get().providerApi?.setPlaybackQuality?.(quality);
          },
          playbackQualities: [],
          repeat: initialRepeat,
          toggleRepeatMode: (enforcePermission = false) => {
            if (enforcePermission) {
              const allowed = ensureUserActionAllowed('repeat', false);
              if (!allowed) return;
            }

            let newRepeat: RepeatMode = 'all';
            const currentRepeat = get().repeat;
            if (currentRepeat === 'all') {
              newRepeat = 'one';
            } else if (currentRepeat === 'one') {
              newRepeat = false;
            }

            if (isNativePlaybackEngineActive(get().playbackEngine)) {
              get().providerApi?.setRepeatMode?.(newRepeat);
              return;
            }

            set({repeat: newRepeat});
            setInLocalStorage('repeat', newRepeat);
          },
          shuffling: initialShuffling,
          toggleShuffling: (enforcePermission = false) => {
            if (enforcePermission) {
              const allowed = ensureUserActionAllowed('shuffle', false);
              if (!allowed) return;
            }

            let newQueue: MediaItem[] = [];

            if (get().shuffling) {
              newQueue = get().originalQueue;
            } else {
              newQueue = shuffleArray([...get().shuffledQueue]);
            }

            if (isNativePlaybackEngineActive(get().playbackEngine)) {
              get().providerApi?.setShuffle?.(!get().shuffling);
              return;
            }

            set(s => {
              s.shuffling = !s.shuffling;
              s.shuffledQueue = newQueue;
            });
          },
          mediaDuration: 0,
          seek: (
            time,
            log_event: boolean = true,
            enforcePermission = false,
          ) => {
            if (enforcePermission) {
              const allowed = ensureUserActionAllowed('seek', false);
              if (!allowed) return;
            }

            const timeStr = `${time}`;
            if (timeStr.startsWith('+')) {
              time = get().getCurrentTime() + Number(time);
            } else if (timeStr.startsWith('-')) {
              time = get().getCurrentTime() - Number(timeStr.replace('-', ''));
            } else {
              time = Number(time);
            }
            get().providerApi?.seek(time);
            get().emit('seek', {time});

            if(log_event) {
              logReactPlaybackEvent({
                event_type: 'seek',
                entity_id: get().cuedMedia?.id,
                entity_type: 'track',
                metadata: {
                  track: {...get().cuedMedia?.meta},
                  time: time,
                  volume: get().volume
                }
              });
            }
          },
          getCurrentTime: () => {
            return get().providerApi?.getCurrentTime() || 0;
          },
          log_event: true,
          play: async (media, log_event: boolean = true) => {
            const nativePlaybackEngineActive = isNativePlaybackEngineActive(
              get().playbackEngine,
            );
            const currentCuedMedia = get().cuedMedia;
            const shouldDelegatePlayToNative =
              Boolean(media) &&
              canRouteMediaToNative(media) &&
              Boolean(get().providerApi?.replaceQueue) &&
              (nativePlaybackEngineActive || Boolean(media));
            let currentAd, playAd = false;
            let currentTIme = get().providerApi?.getCurrentTime() ?? 0;
            
            if (media) {
              if (shouldDelegatePlayToNative) {
                if (!currentCuedMedia || !isSameMedia(media, currentCuedMedia)) {
                  const mirroredQueue = getQueueForNativeMirror(media);
                  await get().providerApi?.replaceQueue?.(
                    mirroredQueue.queue.length ? mirroredQueue.queue : [media],
                    mirroredQueue.startIndex,
                    !hasZeroSecondPreviewLimit,
                  );
                  return;
                }
              }

              if (
                nativePlaybackEngineActive &&
                currentCuedMedia &&
                !isSameMedia(media, currentCuedMedia) &&
                !canRouteMediaToNative(media)
              ) {
                get().providerApi?.stop?.();
              }

              // get currently active queue item, if none is provided
              await get().cue(media);
              
              // Si le media actuel n'est pas une pub, et qu'il y a une puv en attente
              if(
                !nativePlaybackEngineActive &&
                !media?.isAd &&
                ad.isWaiting()
              ) {
                playAd = true;
              }
            } else {
              media = get().cuedMedia || queue.getCurrent();

              if(!nativePlaybackEngineActive && media != currentMedia) {
                playAd = true;
              }
            }

            if(playAd) {
              currentAd = ad.getAd();
              if(currentAd) {
                // -> ajoute la pub juste avant le média qui va être lu
                media = currentAd;
                adItems = [media];
                get().addToQueue(adItems, queue.getPointer()-1);
                await get().cue(media);
                set({
                  providerReady: false,
                  showLoader: true
                });
              }
            }
            
            // if no media to play, stop player and bail
            if (!media) {
              get().stop();
              return;
            }

            if (!media.isAd) {
              const allowed = ensureUserActionAllowed('play', false);
              if (!allowed) return;
              if (!nativePlaybackEngineActive) {
                maybeAutoFillQueue();
              }
            }

            // if(media?.playerType === "video") {
            //   playerOverlayState.open();
            // }
            
            currentMedia = media;
            set({log_event: log_event});

            await options.onBeforePlay?.(currentMedia);
            if (!media?.isAd && hasZeroSecondPreviewLimit) {
              get().openSignUpModal();
              return;
            }
            const activeProviderApi =
              get().providerApi?.play
                ? get().providerApi
                : await waitForActiveProviderApi();
            activeProviderApi?.play?.();
            
            if (isPreviewMode && !nativePlaybackEngineActive) {
              get().openSignUpModal();
            }
            
            if(!media?.isAd && !nativePlaybackEngineActive) {
              log.startTimer(get().cuedMedia);
              ad.startTimer();
            }

            if(!media?.isAd && log_event) {
              logReactPlaybackEvent({
                event_type: 'play',
                entity_id: get().cuedMedia?.id,
                entity_type: 'track',
                metadata: {
                  track: {...get().cuedMedia?.meta},
                  currentTIme: currentTIme,
                  volume: get().volume
                }
              });
            }
          },
          pause: (log_event: boolean = true) => {
            // console.log('pause');
            get().providerApi?.pause();
            log.stopTimer();
            ad.stopTimer();

            if(log_event) {
              let currentTIme = get().providerApi?.getCurrentTime() ?? 0;
              logReactPlaybackEvent({
                event_type: 'pause',
                entity_id: get().cuedMedia?.id,
                entity_type: 'track',
                metadata: {
                  track: {...get().cuedMedia?.meta},
                  currentTIme: currentTIme,
                  volume: get().volume
                }
              });
            }
            
          },
          stop: (log_event: boolean = true) => {
            // console.log('stop');
            log.stopTimer();
            log.resetMedia();
            ad.stopTimer();

            if (isNativePlaybackEngineActive(get().playbackEngine)) {
              get().providerApi?.stop();
              set({isBuffering: false});

              if(log_event) {
                let currentTIme = get().providerApi?.getCurrentTime() ?? 0;
                logReactPlaybackEvent({
                  event_type: 'stop',
                  entity_id: get().cuedMedia?.id,
                  entity_type: 'track',
                  metadata: {
                    track: {...get().cuedMedia?.meta},
                    currentTIme: currentTIme,
                    volume: get().volume
                  }
                });
              }

              return;
            }
            
            get().pause(false);
            get().seek(0, false);
            set({isBuffering: false});
            if (!get().isPlaying) return;

            if(log_event) {
              let currentTIme = get().providerApi?.getCurrentTime() ?? 0;
              logReactPlaybackEvent({
                event_type: 'stop',
                entity_id: get().cuedMedia?.id,
                entity_type: 'track',
                metadata: {
                  track: {...get().cuedMedia?.meta},
                  currentTIme: currentTIme,
                  volume: get().volume
                }
              });
            }
          },
          playNext: async (
            log_event: boolean = true,
            enforceQuota = false,
          ) => {
            if (enforceQuota) {
              const allowed = ensureUserActionAllowed('next', true);
              if (!allowed) return;
            }

            const nativeSkipToNext = get().providerApi?.skipToNext;
            if (
              canDelegateTransportIntentToNative() &&
              nativeSkipToNext
            ) {
              let currentTIme = get().providerApi?.getCurrentTime() ?? 0;

              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'playNext',
                  entity_id: get().cuedMedia?.id,
                  entity_type: 'track',
                  metadata: {
                    track: {...get().cuedMedia?.meta},
                    currentTIme: currentTIme,
                    volume: get().volume
                  }
                });
              }

              maybeLogSkip(get());
              nativeSkipToNext();
              return;
            }

            // console.log('playNext');
            get().stop(false);
            let media = queue.getCurrent();
            let currentTIme = get().providerApi?.getCurrentTime() ?? 0;
            maybeAutoFillQueue();

            if (get().repeat === 'all' && queue.isLast()) {
              media = queue.getFirst();
            } else if (get().repeat !== 'one') {
              media = queue.getNext();
            }

            // YouTube provider will not play the same track unless we wait some time after playback end
            if (get().repeat === 'one' && get().providerName === 'youtube') {
              await new Promise(resolve => setTimeout(resolve, 50));
            }

            // allow user to handle playing next track
            if (options.onBeforePlayNext?.(media)) {
              return;
            }
            
            if (media) {
              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'playNext',
                  entity_id: get().cuedMedia?.id,
                  entity_type: 'track',
                  metadata: {
                    track: {...get().cuedMedia?.meta},
                    currentTIme: currentTIme,
                    volume: get().volume
                  }
                });
              }

              maybeLogSkip(get());

              await get().play(media);
            }
            else {
              get().seek(0, false);
              get().stop(false);
            }
          },
          playPrevious: async (
            log_event: boolean = true,
            enforceQuota = false,
          ) => {
            if (enforceQuota) {
              const allowed = ensureUserActionAllowed('prev', true);
              if (!allowed) return;
            }

            const nativeSkipToPrevious = get().providerApi?.skipToPrevious;
            if (
              canDelegateTransportIntentToNative() &&
              nativeSkipToPrevious
            ) {
              let currentTIme = get().providerApi?.getCurrentTime() ?? 0;

              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'playPrevious',
                  entity_id: get().cuedMedia?.id,
                  entity_type: 'track',
                  metadata: {
                    track: {...get().cuedMedia?.meta},
                    currentTIme: currentTIme,
                    volume: get().volume
                  }
                });
              }

              maybeLogSkip(get());
              nativeSkipToPrevious();
              return;
            }

            // console.log('playPrevious');
            get().stop();
            let media = queue.getCurrent();
            let currentTIme = get().providerApi?.getCurrentTime() ?? 0;

            if(currentTIme < 3) {
              if (get().repeat === 'all' && queue.getPointer() === 0) {
                media = queue.getLast();
              } else if (get().repeat !== 'one') {
                media = queue.getPrevious();
              }
            }
            
            // allow user to handle playing previous track
            if (options.onBeforePlayPrevious?.(media)) {
              return;
            }

            if(log_event) {
              logReactPlaybackEvent({
                event_type: 'playPrevious',
                entity_id: get().cuedMedia?.id,
                entity_type: 'track',
                metadata: {
                  track: media ? {...media} : {...queue.getCurrent()?.meta},
                  currentTIme: currentTIme,
                  volume: get().volume
                }
              });
            }
            
            if (media) {
              maybeLogSkip(get());
              await get().play(media);
            } else {
              get().seek(0, false);
              get().play();
            }
          },
          playQueueItemAtIndex: async (
            index: number,
            log_event: boolean = true,
            enforceQuotaByDirection = false,
          ) => {
            const targetMedia = get().shuffledQueue[index];
            if (!targetMedia) return;

            const currentIndex = queue.getPointer();

            if (enforceQuotaByDirection) {
              if (index > currentIndex) {
                const allowed = ensureUserActionAllowed('next', true);
                if (!allowed) return;
              } else if (index < currentIndex) {
                const allowed = ensureUserActionAllowed('prev', true);
                if (!allowed) return;
              }
            }

            if (index !== currentIndex) {
              maybeLogSkip(get());
            }

            const nativeSkipToIndex = get().providerApi?.skipToIndex;
            if (
              canDelegateTransportIntentToNative() &&
              nativeSkipToIndex
            ) {
              if (hasZeroSecondPreviewLimit) {
                await get().cue(targetMedia, log_event);
                get().openSignUpModal();
                return;
              }
              nativeSkipToIndex(index);
              return;
            }

            await get().play(targetMedia, log_event);
          },
          cue: async (media, log_event: boolean = true) => {
            if (isSameMedia(media, get().cuedMedia)) return;
            
            get().emit('beforeCued', {previous: get().cuedMedia});

            const shouldRouteToNative = canRouteMediaToNative(media);
            const nativeQueueAuthoritativeBeforeCue =
              canDelegateQueueIntentToNative();
            const shouldBootstrapNativeSession =
              shouldRouteToNative && !nativeQueueAuthoritativeBeforeCue;

            if (
              shouldRouteToNative &&
              shouldBootstrapNativeSession &&
              get().providerApi?.replaceQueue
            ) {
              const mirroredQueue = getQueueForNativeMirror(media);
              return get().providerApi?.replaceQueue?.(
                mirroredQueue.queue.length ? mirroredQueue.queue : [media],
                mirroredQueue.startIndex,
                false,
              );
            }

            if (
              shouldRouteToNative &&
              get().providerApi?.cueMedia
            ) {
              return get().providerApi?.cueMedia?.(media);
            }

            return new Promise((resolve, reject) => {
              const previousProvider = get().providerName;
              const previousPlaybackEngine = get().playbackEngine;
              const previousProviderApi = get().providerApi;
              const nextPlaybackEngine: PlaybackEngine = shouldRouteToNative
                ? 'native'
                : 'web';
              const shouldResetProviderApi =
                previousProvider !== media.provider ||
                previousPlaybackEngine !== nextPlaybackEngine;
              const shouldStopPreviousNativeProvider =
                !shouldRouteToNative &&
                isNativePlaybackEngineActive(previousPlaybackEngine);
              let nativeCueTriggered = false;
              
              // wait until media is cued on provider or 3 seconds
              const timeoutId = setTimeout(() => {
                unsubscribeNativeProvider?.();
                unsubscribe();
                resolve();
              }, 3000);
              const unsubscribe = get().subscribe({
                cued: () => {
                  clearTimeout(timeoutId);
                  unsubscribeNativeProvider?.();
                  unsubscribe();
                  if(log_event) {
                    logReactPlaybackEvent({
                      event_type: 'cue',
                      entity_id: media.id,
                      entity_type: 'track',
                      metadata: {
                        track: {...media.meta},
                        volume: get().volume
                      }
                    });
                  }
                  resolve();
                },
                error: e => {
                  clearTimeout(timeoutId);
                  unsubscribeNativeProvider?.();
                  unsubscribe();
                  reject('Could not cue media');
                },
              });
              const syncCueToNative = () => {
                if (!shouldRouteToNative || nativeCueTriggered) {
                  return;
                }

                const nativeProviderApi = get().providerApi;
                const nativeCueMedia = nativeProviderApi?.cueMedia;
                const nativeReplaceQueue = nativeProviderApi?.replaceQueue;
                if (
                  shouldBootstrapNativeSession &&
                  !nativeReplaceQueue
                ) {
                  return;
                }
                if (
                  !shouldBootstrapNativeSession &&
                  !nativeCueMedia
                ) {
                  return;
                }

                nativeCueTriggered = true;
                const mirroredQueue = getQueueForNativeMirror(media);
                const nativeCuePromise = shouldBootstrapNativeSession
                  ? nativeReplaceQueue?.(
                      mirroredQueue.queue.length ? mirroredQueue.queue : [media],
                      mirroredQueue.startIndex,
                      false,
                    )
                  : nativeCueMedia?.(media);
                Promise.resolve(nativeCuePromise).catch(() => {
                  clearTimeout(timeoutId);
                  unsubscribeNativeProvider?.();
                  unsubscribe();
                  reject('Could not cue media');
                });
              };
              const unsubscribeNativeProvider =
                shouldRouteToNative
                  ? store.subscribe(
                      state => state.providerApi,
                      () => syncCueToNative(),
                    )
                  : undefined;
              
              set({
                cuedMedia: media,
                posterUrl: media.poster,
                providerName: media.provider,
                playbackEngine: nextPlaybackEngine,
                providerApi: shouldResetProviderApi ? undefined : get().providerApi,
                providerReady: previousProvider === media.provider,
                controlsLocked: Boolean(media.isAd),
                streamType: 'streamType' in media ? media.streamType : null,
                showLoader: true,
              });

              if (shouldStopPreviousNativeProvider) {
                previousProviderApi?.stop?.();
              }

              if (media) {
                options.setMediaSessionMetadata?.(media);
              }

              if (options.persistQueueInLocalStorage) {
                setInLocalStorage('cuedMediaId', media.id);
              }

              syncCueToNative();
            });
          },
          clearCuedMedia: () => {
            // stoppe proprement le provider si dispo
            get().providerApi?.pause?.();
            get().providerApi?.seek?.(0);

            // reset de la media session (tu l’as déjà importé)
            resetMediaSession();

            // purge l’état player
            set(s => {
              s.cuedMedia = undefined;
              s.posterUrl = undefined;
              s.providerName = undefined as any;
              s.providerReady = false;
              s.controlsLocked = false;
              s.streamType = null;
              s.showLoader = false;
              s.mediaDuration = 0;
            });

            // nettoie la persistence si tu la utilises
            if (options.persistQueueInLocalStorage) {
              _setInLocalStorage(`player.${id}.cuedMediaId`, null);
            }

            // optionnel : prévenir les listeners
            get().emit('uncued');
          },
          async overrideQueue(
            mediaItems: MediaItem[],
            queuePointer: number = 0,
            log_event: boolean = true,
          ): Promise<any> {
            if (!mediaItems?.length) return;
            const items = [...mediaItems];
            const nextQueue = get().shuffling
              ? shuffleArray(items, true)
              : items;
            const media =
              queuePointer > -1 ? mediaItems[queuePointer] : queue.getCurrent();

            if (canDelegateQueueIntentToNative(items)) {
              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'override queue',
                  metadata: {
                    mediaItems: mediaItems,
                    queuePointer: queuePointer,
                    queue: {...nextQueue}
                  }
                });
              }

              return get().providerApi?.replaceQueue?.(
                items,
                queuePointer,
                false,
              );
            }

            set(s => {
              s.shuffledQueue = nextQueue;
              s.originalQueue = items;
            });
            if (options.persistQueueInLocalStorage) {
              setInLocalStorage('queue', get().originalQueue.slice(0, 15));
            }
            
            if(log_event) {
              logReactPlaybackEvent({
                event_type: 'override queue',
                metadata: {
                  mediaItems: mediaItems,
                  queuePointer: queuePointer,
                  queue: {...get().originalQueue}
                }
              });
            }

            if (media) {
              return get().cue(media);
            }
          },
          addToQueue: (mediaItems, index = false, log_event: boolean = true) => {
            if(index === false) return get().appendToQueue(mediaItems);
            
            const shuffledNewItems = get().shuffling
              ? shuffleArray([...mediaItems])
              : [...mediaItems];
            const nextShuffledQueue = prependToArrayAtIndex(
              get().shuffledQueue,
              shuffledNewItems,
              index,
            );
            const nextOriginalQueue = prependToArrayAtIndex(
              get().originalQueue,
              mediaItems,
              index,
            );

            if (canDelegateQueueIntentToNative(nextOriginalQueue)) {
              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'add to queue',
                  metadata: {
                    medias: mediaItems,
                    index: index,
                    queue: {...nextOriginalQueue}
                  }
                });
              }

              return get().providerApi?.addToQueue?.(mediaItems, index);
            }

            set(s => {
              s.shuffledQueue = nextShuffledQueue;
              s.originalQueue = nextOriginalQueue;
            });

            if (options.persistQueueInLocalStorage) {
              setInLocalStorage('queue', get().originalQueue.slice(0, 15));
            }

            if(log_event) {
              logReactPlaybackEvent({
                event_type: 'add to queue',
                metadata: {
                  medias: mediaItems,
                  index: index,
                  queue: {...get().originalQueue}
                }
              });
            }
          },
          appendToQueue: (mediaItems, afterCuedMedia = true, log_event: boolean = true) => {
            const shuffledNewItems = get().shuffling
              ? shuffleArray([...mediaItems])
              : [...mediaItems];
            const index = afterCuedMedia ? queue.getPointer() : 0;
            const nextShuffledQueue = prependToArrayAtIndex(
              get().shuffledQueue,
              shuffledNewItems,
              index,
            );
            const nextOriginalQueue = prependToArrayAtIndex(
              get().originalQueue,
              mediaItems,
              index,
            );

            if (canDelegateQueueIntentToNative(nextOriginalQueue)) {
              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'append to queue',
                  metadata: {
                    medias: mediaItems,
                    afterCuedMedia: afterCuedMedia,
                    queue: {...nextOriginalQueue}
                  }
                });
              }

              return get().providerApi?.appendToQueue?.(
                mediaItems,
                afterCuedMedia,
              );
            }

            set(s => {
              s.shuffledQueue = nextShuffledQueue;
              s.originalQueue = nextOriginalQueue;
            });

            if (options.persistQueueInLocalStorage) {
              setInLocalStorage('queue', get().originalQueue.slice(0, 15));
            }

            if(log_event) {
              logReactPlaybackEvent({
                event_type: 'append to queue',
                metadata: {
                  medias: mediaItems,
                  afterCuedMedia: afterCuedMedia,
                  queue: {...get().originalQueue}
                }
              });
            }
          },
          removeFromQueue: (mediaItems, log_event: boolean = true) => {
            const nextShuffledQueue = get().shuffledQueue.filter(
              item => !mediaItems.find(m => isSameMedia(m, item)),
            );
            const nextOriginalQueue = get().originalQueue.filter(
              item => !mediaItems.find(m => isSameMedia(m, item)),
            );

            if (canDelegateQueueIntentToNative(nextOriginalQueue)) {
              if(log_event) {
                logReactPlaybackEvent({
                  event_type: 'append to queue',
                  metadata: {
                    medias: mediaItems,
                    queue: {...nextOriginalQueue}
                  }
                });
              }

              return get().providerApi?.removeFromQueue?.(mediaItems);
            }

            set(s => {
              s.shuffledQueue = nextShuffledQueue;
              s.originalQueue = nextOriginalQueue;
            });

            if (options.persistQueueInLocalStorage) {
              setInLocalStorage('queue', get().originalQueue.slice(0, 15));
            }

            if(log_event) {
              logReactPlaybackEvent({
                event_type: 'append to queue',
                metadata: {
                  medias: mediaItems,
                  queue: {...get().originalQueue}
                }
              });
            }
          },
          textTracks: [],
          currentTextTrack: -1,
          setCurrentTextTrack: trackId => {
            get().providerApi?.setCurrentTextTrack?.(trackId);
          },
          textTrackIsVisible: false,
          setTextTrackVisibility: isVisible => {
            get().providerApi?.setTextTrackVisibility?.(isVisible);
          },
          audioTracks: [],
          currentAudioTrack: -1,
          setCurrentAudioTrack: trackId => {
            get().providerApi?.setCurrentAudioTrack?.(trackId);
          },
          destroy: () => {
            // console.log("destroy")
            get().destroyFullscreen();
            get().destroyPip();
            options?.onDestroy?.();
            resetMediaSession();
            listeners.clear();
            stopListenQuotaTimer();
            void localQuotaCache.flushToStorage();
            if (typeof document !== 'undefined') {
              document.removeEventListener('keydown', keybindsHandler);
            }
          },
          init: async () => {
            // console.log("init")
            // add initial and listeners from options, these will be present for the entire lifetime of the player
            await localQuotaCache.hydrateFromStorage();

            if (!canUseRepeatControl) {
              setInLocalStorage('repeat', false);
            }
            if (!canUseShuffleControl) {
              setInLocalStorage('shuffling', false);
            }
            if (
              options.persistQueueInLocalStorage &&
              (removedAdItems || clearedNativeAudioQueue)
            ) {
              setInLocalStorage('queue', initialData.queue?.slice(0, 15) ?? []);
              _setInLocalStorage(
                `player.${id}.cuedMediaId`,
                initialData.cuedMediaId ?? null,
              );
            }

            get().initFullscreen();
            get().initPip();
            
            listeners.add(internalListeners);
            if (options.listeners) {
              listeners.add(options.listeners as Partial<ProviderListeners>);
            }

            const mediaId =
              initialData.cuedMediaId || initialData.queue?.[0]?.id;
            const mediaToCue = initialData.queue?.find(
              media => media.id === mediaId,
            );
            const shouldDeferToNativeSession =
              await shouldDeferInitialCueToNativeSession(
                mediaToCue,
                initialData.queue,
              );
            if (shouldDeferToNativeSession) {
              set({
                originalQueue: [],
                shuffledQueue: [],
                providerReady: false,
                showLoader: true,
              });
            }
            if (
              mediaToCue &&
              !shouldDeferToNativeSession &&
              !get().cuedMedia
            ) {
              await get().cue(mediaToCue);
            }
            if (shouldReactOwnPlaybackLogic()) {
              initPlayerMediaSession(get, options);
            }
            if (typeof document !== 'undefined') {
              document.addEventListener('keydown', keybindsHandler);
            }
          },
          subscribe: newListeners => {
            listeners.add(newListeners);
            return () => listeners.delete(newListeners);
          },
          emit(event, payload?: any) {
            listeners.forEach(l => l[event]?.({state: get(), ...payload}));
          },
          isSignUpModalOpen: false,
          openSignUpModal() {
            set({ isSignUpModalOpen: true })
          },
          closeSignUpModal() {
            activeNativeLimitPromptSignature = null;
            set({ isSignUpModalOpen: false })
          },
          handleNativeLimitReached,
        };
      }),
    )
  );
};
